import { MenuItemGroup } from '@creditornot/cb-menu';
import { UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { localize } from 'common/utils';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { SelectionCategory } from 'modules/shared-menu/types';

import NavigationItem from './NavigationItem';
import { DndCategoryItem } from './categories/DndCategoryItem';
import { DndParentCategoryItem } from './subcategories/DnDParentCategoryItem';
import { useSelectionData } from '../../../../hooks/useSelectionData';
import { useWoltSpecials } from '../../context/WoltSpecialsContext';
import CategoryNavigationDndMonitor from '../dnd/categories/CategoriesDndMonitor';

type Props = {
  menuEditorRootUrl: string;
  categories: SelectionCategory[];
};

const SharedMenuCategoryNavItems = ({ menuEditorRootUrl, categories }: Props) => {
  const { selection } = useSelectionData();
  const { woltSpecials: hasWoltSpecialsPermission } = useOfferingMenuPermissionsContext();
  const { woltSpecialsCategory } = useWoltSpecials();

  return (
    <MenuItemGroup title={<FormattedMessage id="menu.categories" />}>
      {hasWoltSpecialsPermission && woltSpecialsCategory && (
        <NavigationItem
          style={{ marginBlock: 8 }}
          label={localize(woltSpecialsCategory.name, selection.primary_language)}
          numberOfItems={woltSpecialsCategory.offer_bindings?.length ?? 0}
          href={`${menuEditorRootUrl}/${woltSpecialsCategory.id}`}
          labelSideTag="Wolt Specials"
        />
      )}
      <CategoryNavigationDndMonitor categories={categories}>
        {({ displayCategories, isSortingCategory }) => {
          const filteredDisplayCategories = displayCategories.filter(category => !category.is_wolt_special);

          const itemUniqueIdentifiers: UniqueIdentifier[] = filteredDisplayCategories.map(category => category.id);
          return (
            <SortableContext items={itemUniqueIdentifiers} strategy={verticalListSortingStrategy}>
              {filteredDisplayCategories.map(category =>
                category.subcategories && category.subcategories.length > 0 ? (
                  <DndParentCategoryItem
                    category={category}
                    disabledSubcategorySort={isSortingCategory}
                    key={category.id}
                    language={selection.primary_language}
                    menuEditorRootUrl={menuEditorRootUrl}
                  />
                ) : (
                  <DndCategoryItem
                    category={category}
                    key={category.id}
                    language={selection.primary_language}
                    menuEditorRootUrl={menuEditorRootUrl}
                  />
                ),
              )}
            </SortableContext>
          );
        }}
      </CategoryNavigationDndMonitor>
    </MenuItemGroup>
  );
};

export default React.memo(SharedMenuCategoryNavItems);
